import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  loadMoreColumnItemsThunk,
  reloadColumnItemsThunk,
} from '../columnPagination/columnPagination.thunks'
import {
  createInitialColumnSortState,
  type TaskTrackerColumnSortState,
} from './columnSort.types'

const columnSortSlice = createSlice({
  name: 'taskTrackerColumnSort',
  initialState: {} as import('./columnSort.types').ColumnSortSliceState,
  reducers: {
    resetColumnSort(state, action: PayloadAction<{ boardId: number }>) {
      state[action.payload.boardId] = {}
    },
    syncColumnSortColumns(
      state,
      action: PayloadAction<{ boardId: number; columnIds: number[] }>,
    ) {
      const { boardId, columnIds } = action.payload
      if (!state[boardId]) {
        state[boardId] = {}
      }
      const boardSort = state[boardId]
      const columnIdSet = new Set(columnIds)

      columnIds.forEach((columnId) => {
        if (!boardSort[columnId]) {
          boardSort[columnId] = createInitialColumnSortState()
        }
      })

      Object.keys(boardSort).forEach((rawColumnId) => {
        const columnId = Number(rawColumnId)
        if (!columnIdSet.has(columnId)) {
          delete boardSort[columnId]
        }
      })
    },
    setColumnSort(
      state,
      action: PayloadAction<{
        boardId: number
        columnId: number
        sort: Partial<TaskTrackerColumnSortState>
      }>,
    ) {
      const { boardId, columnId, sort } = action.payload
      if (!state[boardId]) {
        state[boardId] = {}
      }
      const prev = state[boardId][columnId] ?? createInitialColumnSortState()
      state[boardId][columnId] = { ...prev, ...sort }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state[action.payload.boardId]
      })
      .addCase(reloadColumnItemsThunk.pending, (state, action) => {
        const { boardId, columnId } = action.meta.arg
        if (!state[boardId]) {
          state[boardId] = {}
        }
        const prev = state[boardId][columnId] ?? createInitialColumnSortState()
        state[boardId][columnId] = { ...prev, reloading: true }
      })
      .addCase(reloadColumnItemsThunk.fulfilled, (state, action) => {
        const { boardId, columnId } = action.payload
        if (!state[boardId]?.[columnId]) return
        state[boardId][columnId] = {
          ...state[boardId][columnId],
          reloading: false,
        }
      })
      .addCase(reloadColumnItemsThunk.rejected, (state, action) => {
        const { boardId, columnId } = action.meta.arg
        if (!state[boardId]?.[columnId]) return
        state[boardId][columnId] = {
          ...state[boardId][columnId],
          reloading: false,
        }
      })
      .addCase(loadMoreColumnItemsThunk.pending, () => {})
  },
})

export const { resetColumnSort, syncColumnSortColumns, setColumnSort } =
  columnSortSlice.actions

export default columnSortSlice.reducer
